/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useState } from "react";
import { Image, TouchableOpacity, View, ScrollView } from "react-native";
import parse from "html-react-parser";
import SwipeableViews from "react-swipeable-views";
import Icon from "react-icons-kit";
import { phoneOff } from "react-icons-kit/feather/phoneOff";
import { moreVertical } from "react-icons-kit/feather/moreVertical";
import { xCircle } from "react-icons-kit/feather/xCircle";
import { arrowLeft } from "react-icons-kit/feather/arrowLeft";
import { arrowRight } from "react-icons-kit/feather/arrowRight";
import { clock } from "react-icons-kit/feather/clock";

import { Text, styles } from "UpduoLibrary";
import {
  colorWithOpacity,
  skaleBlack,
  skaleBorderLightGray,
  skaleLightGray,
  skaleWhite,
} from "config/UpduoColors";
import { processSkillString } from "config/Utilities";
import PartnerA from "assets/partnerA.png";
import PartnerB from "assets/partnerB.png";

const chatPadding = 20;
const buttonsHeight = 50;

function StepCardPreview({ step, isTeacher, stepIndex, sessionLength }) {
  const [isDetailShown, setIsDetailShown] = useState(false);
  const stepContent = step[isTeacher ? "teacher" : "learner"];

  const detailHtml = stepContent.detail;
  const detailButton = (
    <TouchableOpacity
      style={{
        backgroundColor: "#1e003c",
        alignItems: "center",
        justifyContent: "center",
        paddingHorizontal: 30,
        paddingVertical: 10,
        borderRadius: 5,
      }}
      onPress={() => {
        setIsDetailShown(true);
      }}
    >
      <Text isHeading={true} style={{ color: skaleWhite, fontSize: 16 }}>
        {"More"}
      </Text>
    </TouchableOpacity>
  );

  const cardContent = (
    <View style={{ flex: 1, marginHorizontal: 20 }}>
      <View
        style={{
          overflow: "hidden",
          paddingTop: 24,
          paddingBottom: 10,
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Text
          style={{
            color: skaleWhite,
            fontSize: 16,
            fontWeight: "bold",
          }}
        >
          {`${stepIndex + 1}/${sessionLength}`}
        </Text>
        <View
          style={{
            flex: 1,
            marginHorizontal: 5,
            height: 10,
            borderRadius: 5,
            flexDirection: "row",
            backgroundColor: "#1e003c",
          }}
        >
          <View
            style={{
              flex: stepIndex,
              backgroundColor: "#ffa631",
              borderRadius: 5,
            }}
          />
          <View style={{ flex: sessionLength - stepIndex }} />
        </View>
        <Text
          style={{
            fontSize: 16,
            color: skaleWhite,
          }}
        >
          <Icon size={15} icon={clock} />
          {`  ${step.minutes} min${step.minutes > 1 ? "s" : ""}`}
        </Text>
      </View>
      <View style={{ flex: 1, justifyContent: "space-between" }}>
        <ScrollView contentContainerStyle={{ flex: 1 }}>
          <Text
            style={{
              fontSize: 16,
              color: skaleWhite,
            }}
          >
            {stepContent.heading}
          </Text>
        </ScrollView>
        <View style={{ alignItems: "center", margin: 10 }}>
          {detailHtml ? detailButton : null}
        </View>
      </View>
    </View>
  );

  const detailContent = (
    <View style={{ flex: 1 }}>
      <ScrollView style={{ flex: 1 }}>
        <View style={{ marginHorizontal: 20 }}>
          <Text
            style={{
              fontSize: 16,
              color: skaleWhite,
            }}
          >
            {parse(detailHtml)}
          </Text>
        </View>
      </ScrollView>
      <TouchableOpacity
        style={{
          height: 45,
          backgroundColor: "#1e003c",
          alignItems: "center",
        }}
        onPress={() => setIsDetailShown(false)}
      >
        <Text style={{ color: skaleWhite, margin: 5 }}>
          <Icon size={20} icon={xCircle} />
        </Text>
      </TouchableOpacity>
    </View>
  );

  return (
    <View
      style={{
        height: 680,
        justifyContent: "flex-end",
        overflow: "hidden",
      }}
    >
      <View
        style={{
          backgroundColor: "#32005c",
          height: 230,
          margin: chatPadding,
          overflow: "hidden",
          borderRadius: 18,
        }}
      >
        {isDetailShown ? detailContent : cardContent}
      </View>
    </View>
  );
}

interface PhonePreviewProps {
  skillSessionSteps: any[];
  teacherRole: string;
  learnerRole: string;
  skillName: string;
  isTeacher: boolean;
  title: string;
  cardIndex: number;
  communityName: string;
  onChangeIndex?: (index: number) => void;
}
export default function PhonePreview({
  skillSessionSteps,
  teacherRole,
  learnerRole,
  skillName,
  isTeacher,
  title,
  cardIndex,
  communityName,
  onChangeIndex = () => {},
}: PhonePreviewProps) {
  const newSkillSessionSteps = JSON.parse(JSON.stringify(skillSessionSteps));
  // Perform string replacements for all relevant step card fields
  const roles = ["teacher", "learner"];
  const stringTypes = ["heading", "detail"];
  for (let i = 0; i < newSkillSessionSteps.length; i++) {
    for (const role of roles) {
      for (const stringType of stringTypes) {
        newSkillSessionSteps[i][role][stringType] = processSkillString(
          newSkillSessionSteps[i][role][stringType],
          "Alice",
          "Bob",
          teacherRole,
          learnerRole,
          skillName,
          isTeacher,
          communityName
        );
      }
    }
  }

  const sessionStepCards = newSkillSessionSteps.map((step, stepIndex) => {
    return (
      <StepCardPreview
        step={step}
        key={stepIndex}
        stepIndex={stepIndex}
        isTeacher={isTeacher}
        sessionLength={newSkillSessionSteps.length}
      />
    );
  });

  const phoneWidth = 350;
  const phoneHeight = 750;
  return (
    <View style={{ alignItems: "center", paddingTop: 40 }}>
      <View style={[styles.skillRoleHeading]}>
        <View
          style={{
            width: 40,
            height: 40,
            borderRadius: 20,
            backgroundColor: skaleLightGray,
            overflow: "hidden",
          }}
        >
          <Image
            source={isTeacher ? PartnerB : PartnerA}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: 40,
              height: 60,
            }}
          />
        </View>
        <Text
          style={{
            color: skaleBlack,
            fontSize: 16,
            fontWeight: "500",
            margin: 12,
          }}
        >
          {title}
        </Text>
      </View>
      <View
        style={{
          height: phoneHeight,
          width: phoneWidth,
          overflow: "hidden",
          borderWidth: 1,
          borderColor: skaleBorderLightGray,
          borderRadius: 18,
        }}
      >
        <Image
          source={isTeacher ? PartnerA : PartnerB}
          style={{
            width: phoneWidth,
            height: phoneHeight,
            resizeMode: "cover",
          }}
        />
        <View
          style={{
            position: "absolute",
            top: chatPadding,
            left: chatPadding,
            right: chatPadding,
            height: 60,
            borderRadius: 30,
            backgroundColor: colorWithOpacity(skaleBlack, 0.5),
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Text
            style={{
              color: skaleWhite,
              paddingHorizontal: 20,
            }}
          >
            <Icon size={20} icon={phoneOff} />
          </Text>
          <Text
            style={{ color: skaleWhite, textAlign: "center" }}
            ellipsizeMode={"tail"}
            numberOfLines={1}
          >
            {skillName}
          </Text>
          <Text
            style={{
              color: skaleWhite,
              paddingHorizontal: 20,
            }}
          >
            <Icon size={20} icon={moreVertical} />
          </Text>
        </View>
        <Image
          style={{
            position: "absolute",
            overflow: "hidden",
            top: 100,
            right: chatPadding,
            width: 70,
            height: 110,
            borderWidth: 1,
            borderBottomWidth: 0,
            borderColor: skaleBorderLightGray,
            borderRadius: 8,
            backgroundColor: skaleWhite,
            resizeMode: "cover",
          }}
          source={isTeacher ? PartnerB : PartnerA}
        />
        <View
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: phoneWidth,
            height: phoneHeight,
          }}
        >
          <SwipeableViews
            disabled={!isTeacher}
            index={cardIndex}
            onChangeIndex={(newIndex) => {
              if (isTeacher && onChangeIndex) {
                onChangeIndex(newIndex);
              }
            }}
          >
            {sessionStepCards}
          </SwipeableViews>
          <View
            style={{
              flexDirection: "row",
              paddingHorizontal: chatPadding,
              justifyContent: "space-between",
            }}
          >
            <View
              style={{
                width: 145,
                height: buttonsHeight,
                borderRadius: 10,
                backgroundColor: "white",
                justifyContent: "center",
              }}
            >
              <Text
                style={{
                  color: "#6715af",
                  fontSize: 16,
                  textAlign: "center",
                  lineHeight: buttonsHeight,
                }}
              >
                <Icon
                  style={{ marginBottom: 3, marginRight: 5 }}
                  size={20}
                  icon={arrowLeft}
                />
                {"Back"}
              </Text>
            </View>
            <View
              style={{
                width: 145,
                height: buttonsHeight,
                borderRadius: 10,
                backgroundColor:
                  cardIndex + 1 >= sessionStepCards.length
                    ? "#ffa631"
                    : "#6715af",
                justifyContent: "center",
              }}
            >
              <Text
                style={{
                  color:
                    cardIndex + 1 >= sessionStepCards.length
                      ? "#6715af"
                      : skaleWhite,
                  fontSize: 16,
                  textAlign: "center",
                  lineHeight: buttonsHeight,
                }}
              >
                {cardIndex + 1 >= sessionStepCards.length ? "End" : "Next"}
                <Icon
                  style={{ marginBottom: 3, marginLeft: 5 }}
                  size={20}
                  icon={arrowRight}
                />
              </Text>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
}
